import { Add, Edit } from '@mui/icons-material';
import { Button, Divider, FormControlLabel, Box, Switch, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button as SMButton,
  OrderManagementPage,
  PageTitleWithButton,
  Table
} from '../../../components';
import { UserContext } from '../../../context';
import { getPlaceOrderTypes } from '../../../fetch';
import { IOrderType } from '../../../models';
import { MobileOrderType } from './MobileOrderType';
import { OrderTypeModal } from './OrderTypeModal';

export const OrderTypes = () => {
  const { isBillingAdmin, isGlobalAdmin, isFinancialAdmin } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [currentOrderType, setCurrentOrderType] = useState<IOrderType | null>(null);
  const [orderTypes, setOrderTypes] = useState<IOrderType[]>([]);
  const [isLoadingOrderTypes, setLoadingOrderTypes] = useState<boolean>(false);
  const [isActive, setActive] = useState<boolean>(true);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);

  const isSmMobile = useMediaQuery('(max-width: 600px)');

  const fetchOrderTypes = async () => {
    try {
      setLoadingOrderTypes(true);
      const res = await getPlaceOrderTypes({
        isActive
      });
      setOrderTypes(res);
    } catch (error) {
      enqueueSnackbar('Error loading order types, please refresh and try again.', {
        variant: 'error'
      });
      console.error(error);
    } finally {
      setLoadingOrderTypes(false);
    }
  };

  useEffect(() => {
    fetchOrderTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const handleEdit = (type: IOrderType) => {
    setModalOpen(true);
    setCurrentOrderType(type);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'typeName',
        sort: false
      },
      {
        Header: 'Description',
        accessor: 'additionalDetails',
        sort: false
      },
      (isBillingAdmin || isFinancialAdmin || isGlobalAdmin) && {
        id: 'actions',
        Header: '',
        accessor: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IOrderType } };
        }) => {
          return (
            <Box textAlign="right">
              <Button
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  handleEdit(original);
                }}
              >
                Edit
              </Button>
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderTypes]);

  return (
    <OrderManagementPage title="Order Types">
      <PageTitleWithButton
        pageTitleProps={{
          marginBottom: false,
          showDivider: false,
          title: 'Order Types'
        }}
        button={
          (isBillingAdmin || isFinancialAdmin || isGlobalAdmin) && (
            <Box display="flex" gap={1}>
              <Box mr={-1}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={isActive}
                      id="active-inactive"
                      disabled={isLoadingOrderTypes}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setActive(e.target.checked);
                      }}
                    />
                  }
                  label="Active"
                />
              </Box>
              <Divider orientation="vertical" flexItem />
              <SMButton
                disabled={isLoadingOrderTypes}
                id="create-new-template"
                startIcon={<Add />}
                onClick={() => setModalOpen(true)}
                color="primary"
                variant="contained"
              >
                Add New Order Type
              </SMButton>
            </Box>
          )
        }
      />
      <Table
        columns={columns}
        data={orderTypes}
        isLoading={isLoadingOrderTypes}
        hidePagination
        ResponsiveComponent={isSmMobile ? MobileOrderType : undefined}
        mobileProps={{
          handleEdit: (type: IOrderType) => handleEdit(type)
        }}
      />
      <OrderTypeModal
        open={isModalOpen}
        onClose={() => {
          setCurrentOrderType(null);
          setModalOpen(false);
        }}
        fetchOrderTypes={fetchOrderTypes}
        currentOrderType={currentOrderType}
      />
    </OrderManagementPage>
  );
};
